import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTasks, FaExclamationTriangle, FaArrowLeft, FaChevronDown } from 'react-icons/fa';
import axiosInstance from './api/axios';
import Header from './Header'; 
import { useAuth } from './AuthContext';

const ALL_INTERFACES = {
    "Drop 1 - Master Data": [
        { key: "merchandising_product_groups" }, { key: "locations" }, { key: "product_groups" }, { key: "product_locations" }, { key: "products" }, { key: "promotion_assortments" }, { key: "campaigns" }, { key: "price_calendars" }, { key: "product_location_price_calendars" }, { key: "sales_prices" }, { key: "purchase_prices" }, { key: "competitor_prices" }, { key: "competitor_campaigns" }, { key: "product_location_campaigns" }, { key: "location_hierarchy" }, { key: "opening_times" }, { key: "assortments" }, { key: "product_location_category_assortment" }, { key: "end_customers" }, { key: "product_end_customers" }, { key: "product_images" }, { key: "floorplan_and_planogram_migrations" }, { key: "delivery_schedules" }, { key: "product_batch_sizes" }, { key: "product_location_batch_sizes" }, { key: "product_location_suppliers" }, { key: "suppliers" }, { key: "supplier_fundings" }, { key: "merchandising_product_catalogues" }, { key: "floor_plans" }, { key: "location_category_spaces" }, { key: "planogram_positions" }, { key: "product_location_supplier_replenishment_agreements" }, { key: "replenishment_agreements" }, { key: "product_location_coordination_groups" }, { key: "coordination_groups" }, { key: "constraint_groups" }, { key: "supplier_locations" }, { key: "brackets" }, { key: "supplier_location_brackets" }, { key: "coordination_group_brackets" }, { key: "product_replacements_and_refs" }, { key: "prepack_variants" }, { key: "supplier_closures" }, { key: "category_clusters" }, { key: "bill_of_materials" }, { key: "barcodes" }
    ],
    "Drop 2 - Historical Data": [
        { key: "historical_balances" }, { key: "historical_sales" }, { key: "historical_deliveries" }, { key: "historical_spoilages" }, { key: "historical_transfers" }, { key: "historical_inventory_checks" }, { key: "historical_adjustments" }, { key: "historical_lost_sales" }, { key: "historical_campaigns" }, { key: "historical_product_location_campaigns" }, { key: "historical_price_calendars" }, { key: "historical_sales_prices" }, { key: "historical_purchase_prices" }, { key: "historical_supplier_fundings" }, { key: "historical_promotion_assortments" }, { key: "historical_footfalls" }
    ],
    "Drop 3 - Transactional Data": [
        { key: "sales" }, { key: "end_customer_transactions" }, { key: "balances" }, { key: "batch_balances" }, { key: "open_purchase_orders" }, { key: "open_sales_orders" }, { key: "deliveries" }, { key: "spoilages" }, { key: "transfers" }, { key: "inventory_checks" }, { key: "adjustments" }, { key: "lost_sales" }, { key: "footfalls" }, { key: "dc_transactions" }, { key: "orders" }
    ],
    "Drop 4 - Output Interfaces": [
        { key: "output_order_proposals" }, { key: "output_reserve_order_proposals" }, { key: "output_sales_forecasts" }, { key: "output_clearance" }, { key: "output_workload_driver_forecast_day_level" }, { key: "output_workload_driver_forecast_15_min_level" }, { key: "output_shelf_edge_labels" }, { key: "output_inventory_exceptions" }, { key: "output_promotions" }, { key: "output_prices" }, { key: "output_assortments" }
    ]
};

const getDropForInterface = (interfaceKey) => {
    for (const [dropName, interfaces] of Object.entries(ALL_INTERFACES)) {
        if (interfaces.some(int => int.key === interfaceKey)) {
            return dropName;
        }
    }
    return "Custom / Assigned Drop"; // --- APPLIED FIX: Cleaner fallback for custom interfaces
};

const LoadingSpinner = ({ text = 'Loading...' }) => (
    <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>{text}</p>
    </div>
);

const AccessDenied = () => (
    <div className="access-denied-container">
        <FaExclamationTriangle className="access-denied-icon" />
        <h2 className="access-denied-title">Access Denied</h2>
        <p>You do not have the required permissions to view this page.</p>
    </div>
);

export default function InterfaceManagement() {
    const navigate = useNavigate();
    const { user, selectedProjectName } = useAuth();
    const userProjects = user?.projects || [];

    const [scheduleData, setScheduleData] = useState([]);
    const [projectDetails, setProjectDetails] = useState(null);
    const [interfaceStatuses, setInterfaceStatuses] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userRoleChecked, setUserRoleChecked] = useState(false);
    const [updatingTasks, setUpdatingTasks] = useState({});

    useEffect(() => {
        if (!user) {
            return;
        }
        setUserRoleChecked(true); 
        if (!selectedProjectName) {
            setLoading(false);
        }
    }, [user, selectedProjectName]);

    const fetchProjectData = useCallback(async (projectName) => {
        setLoading(true);
        setError(null);
        try {
            const [scheduleRes, projectRes, statusesRes] = await Promise.all([
                axiosInstance.get(`/api/projects/${projectName}/schedule?include_inactive=true`),
                axiosInstance.get(`/projects/${projectName}`),
                axiosInstance.get(`/api/projects/${projectName}/interfaces`)
            ]);

            setScheduleData(scheduleRes.data);
            setProjectDetails(projectRes.data);
            setInterfaceStatuses(statusesRes.data);
            setLoading(false); 
        } catch (err) {
            if (err.response?.status === 401) return;

            setError(err.response?.data?.detail || `Failed to load data for ${projectName}.`);
            setScheduleData([]);
            setProjectDetails(null);
            setInterfaceStatuses([]);
            setLoading(false); 
        }
    }, []);

    useEffect(() => {
        if (selectedProjectName) {
            fetchProjectData(selectedProjectName);
        } else if (userRoleChecked) { 
            setScheduleData([]);
            setProjectDetails(null);
            setInterfaceStatuses([]);
            setLoading(false); 
        }
    }, [selectedProjectName, userRoleChecked, fetchProjectData]);

    const processedData = useMemo(() => {
        if (!scheduleData) return [];

        const parsedDrops = scheduleData.map(drop => {
            const interfaces = drop.tasks.reduce((acc, task) => {
                if (!acc[task.interface_name]) {
                    acc[task.interface_name] = {};
                }
                acc[task.interface_name][task.task_name] = task;
                return acc;
            }, {});

            return {
                dropName: drop.name,
                interfaces: Object.entries(interfaces).map(([name, tasks]) => ({ interfaceName: name, tasks }))
            };
        });

        const cancelledStatuses = interfaceStatuses.filter(s => s.cancellation_reason);

        if (cancelledStatuses.length > 0) {
            const cancelledInterfacesObj = {};
            cancelledStatuses.forEach(status => {
                cancelledInterfacesObj[status.interface_name] = {};
            });

            parsedDrops.push({
                dropName: "Cancelled Interfaces",
                interfaces: Object.entries(cancelledInterfacesObj).map(([name, tasks]) => ({ interfaceName: name, tasks }))
            });
        }

        return parsedDrops;
    }, [scheduleData, interfaceStatuses]);

    const handleStatusChange = async (interfaceData, taskName, newStatus) => {
        const task = interfaceData.tasks[taskName];
        if (!task || task.status === newStatus) return;

        let updates = [{ taskId: task.id, newStatus }];

        const cascadeResetMap = {
            "Sample Delivery": ["Technical Validation", "Full Extraction", "Connect Integration", "Upload in RELEX", "Functional Validation"],
            "Technical Validation": ["Full Extraction", "Connect Integration", "Upload in RELEX", "Functional Validation"],
            "Full Extraction": ["Connect Integration", "Upload in RELEX", "Functional Validation"],
            "Connect Integration": [],
            "Upload in RELEX": [],
            "Functional Validation": []
        };

        if (newStatus !== 'Completed') {
            const tasksToReset = cascadeResetMap[taskName] || [];
            tasksToReset.forEach(subTaskName => {
                const dependentTask = interfaceData.tasks[subTaskName];
                if (dependentTask && dependentTask.status !== 'Pending') {
                    updates.push({ taskId: dependentTask.id, newStatus: 'Pending' });
                }
            });
        }

        setUpdatingTasks(prev => ({ ...prev, [interfaceData.interfaceName]: true }));

        try {
            const updatePromises = updates.map(u =>
                axiosInstance.put(`/api/projects/${selectedProjectName}/tasks/${u.taskId}/status`, {
                    status: u.newStatus
                })
            );
            await Promise.all(updatePromises);
            await fetchProjectData(selectedProjectName); 

            setUpdatingTasks(prev => {
                const newUpdating = { ...prev };
                delete newUpdating[interfaceData.interfaceName];
                return newUpdating;
            });
        } catch (err) {
            if (err.response?.status === 401) return;

            alert(`Error: ${err.response?.data?.detail || 'Failed to update task status.'}`);
            await fetchProjectData(selectedProjectName);

            setUpdatingTasks(prev => {
                const newUpdating = { ...prev };
                delete newUpdating[interfaceData.interfaceName];
                return newUpdating;
            });
        }
    };

    if (!userRoleChecked) return <div className="admin-page-container"><LoadingSpinner text="Verifying permissions..." /></div>;

    if (user && !['admin', 'editor', "viewer", 'manager'].includes(user.role)) return <div className="admin-page-container"><AccessDenied /></div>;

    const getStatusClass = (status) => {
        if (!status) return 'pending';
        return status.toLowerCase().replace(/ /g, '-');
    };

    return (
        <div className="admin-page-container">
            <Header />

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/home')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>

                    <div className="header-title-group">
                        <h1 className="page-title">Interface Task Management</h1>
                        {selectedProjectName && (
                            <div className="project-context-inline">
                                <span className="separator">|</span>
                                <span className="context-label">Project:</span>
                                <span className="project-highlight">{selectedProjectName}</span>
                            </div>
                        )}
                    </div>

                    <div className="page-disclaimer">
                        <FaTasks />
                        <span>Task statuses are dependent. You must complete preceding tasks (e.g., "Full Extraction") before beginning parallel branch tasks.</span>
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <div className="form-feedback error">{error}</div>
                ) : !selectedProjectName && userProjects.length > 0 ? (
                    <div className="info-message"><p>Please select a project from the header to begin.</p></div>
                ) : !selectedProjectName && userProjects.length === 0 ? (
                    <div className="info-message"><p>You are not assigned to any projects.</p></div>
                ) : (
                    <div className="drops-container">
                        {processedData && processedData.length > 0 ? processedData.map(drop => (
                            <section key={drop.dropName} className="drop-section">
                                <h2 className="drop-title">{drop.dropName}</h2>
                                <div className="table-wrapper">
                                    {drop.dropName === "Cancelled Interfaces" ? (
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Interface</th>
                                                    <th>Original Assignment</th>
                                                    <th>Status</th>
                                                    <th style={{ textAlign: 'left' }}>Cancellation Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {drop.interfaces.map(iface => {
                                                    const statusInfo = interfaceStatuses.find(s => s.interface_name === iface.interfaceName);
                                                    return (
                                                        <tr key={iface.interfaceName} className="row-inactive">
                                                            {/* --- APPLIED FIX: Capitalize Interface Name --- */}
                                                            <td data-label="Interface" className="interface-name-cell">
                                                                {iface.interfaceName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                                            </td>
                                                            <td data-label="Original Assignment" style={{ textAlign: 'center', color: 'var(--light-text)' }}>
                                                                <span className="drop-badge">
                                                                    {getDropForInterface(iface.interfaceName).split(' - ')[0]}
                                                                </span>
                                                            </td>
                                                            <td data-label="Status" style={{ textAlign: 'center' }}>
                                                                <span className="status-badge cancelled">Cancelled</span>
                                                            </td>
                                                            <td data-label="Cancellation Comment" className="cancellation-comment-cell">
                                                                {statusInfo?.cancellation_reason || ''}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <table className="admin-table">
                                            <thead>
                                                <tr>
                                                    <th>Interface</th>
                                                    <th>Sample Delivery</th>
                                                    <th>Technical Validation</th>
                                                    <th>Full Extraction</th>
                                                    <th>Connect Integration</th>
                                                    <th>Upload in RELEX</th>
                                                    <th>Functional Validation</th>
                                                    <th>Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {drop.interfaces.map(iface => {
                                                    const isUpdating = updatingTasks[iface.interfaceName];
                                                    const tasks = iface.tasks;

                                                    const statusInfo = interfaceStatuses.find(s => s.interface_name === iface.interfaceName);
                                                    const isCancelled = !!statusInfo?.cancellation_reason;

                                                    // --- APPLIED FIX: Safely determine if interface is standard vs custom ---
                                                    const isStandardInterface = Object.values(ALL_INTERFACES).flat().some(i => i.key === iface.interfaceName);
                                                    
                                                    // If it's standard, check projectDetails. If it's custom, ALWAYS treat it as attached!
                                                    const isInterfaceAttached = isStandardInterface 
                                                        ? (projectDetails ? projectDetails[iface.interfaceName] !== false : true)
                                                        : true;

                                                    if (!isInterfaceAttached && !isCancelled) {
                                                        return null;
                                                    }

                                                    const sampleDeliveryStatus = tasks['Sample Delivery']?.status || 'Pending';
                                                    const techValidationStatus = tasks['Technical Validation']?.status || 'Pending';
                                                    const fullExtractionStatus = tasks['Full Extraction']?.status || 'Pending';
                                                    const integrationStatus = tasks['Connect Integration']?.status || 'Pending';
                                                    const relexStatus = tasks['Upload in RELEX']?.status || 'Pending';
                                                    const functionalStatus = tasks['Functional Validation']?.status || 'Pending';

                                                    const isAdmin = user?.role === 'admin';
                                                    const isEditor = user?.role === 'editor';
                                                    const isManager = user?.role === 'manager';

                                                    return (
                                                        <tr
                                                            key={iface.interfaceName}
                                                            className={`${isUpdating ? 'row-updating' : ''} ${isCancelled ? 'row-inactive' : ''}`}
                                                        >
                                                            {/* --- APPLIED FIX: Capitalize Interface Name --- */}
                                                            <td data-label="Interface" className="interface-name-cell">
                                                                {iface.interfaceName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                                                            </td>
                                                            
                                                            {/* 1. Sample Delivery */}
                                                            <td data-label="Sample Delivery">
                                                                {!isCancelled && (
                                                                    <div className="status-select-wrapper">
                                                                        <select
                                                                            value={sampleDeliveryStatus}
                                                                            onChange={(e) => handleStatusChange(iface, 'Sample Delivery', e.target.value)}
                                                                            disabled={isUpdating || !(isAdmin || isEditor || isManager)}
                                                                            className={`status-select ${getStatusClass(sampleDeliveryStatus)}`}
                                                                        >
                                                                            <option value="Pending">Not Started</option>
                                                                            <option value="In Progress">In Progress</option>
                                                                            <option value="Completed">Completed</option>
                                                                        </select>
                                                                        <FaChevronDown className="status-select-arrow" />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* 2. Technical Validation */}
                                                            <td data-label="Technical Validation">
                                                                {!isCancelled && (
                                                                    <div className="status-select-wrapper">
                                                                        <select
                                                                            value={techValidationStatus}
                                                                            onChange={(e) => handleStatusChange(iface, 'Technical Validation', e.target.value)}
                                                                            disabled={isUpdating || !(isAdmin || isManager) || sampleDeliveryStatus === 'Pending'}
                                                                            className={`status-select ${getStatusClass(techValidationStatus)}`}
                                                                            title={sampleDeliveryStatus === 'Pending' ? 'Sample Delivery must be started first' : ''}
                                                                        >
                                                                            <option value="Pending">Not Started</option>
                                                                            <option value="In Progress">In Progress</option>
                                                                            {sampleDeliveryStatus === 'Completed' && (
                                                                                <option value="Completed">Completed</option>
                                                                            )}
                                                                        </select>
                                                                        <FaChevronDown className="status-select-arrow" />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* 3. Full Extraction */}
                                                            <td data-label="Full Extraction">
                                                                {!isCancelled && (
                                                                    <div className="status-select-wrapper">
                                                                        <select
                                                                            value={fullExtractionStatus}
                                                                            onChange={(e) => handleStatusChange(iface, 'Full Extraction', e.target.value)}
                                                                            disabled={isUpdating || !(isAdmin || isEditor || isManager) || techValidationStatus === 'Pending'}
                                                                            className={`status-select ${getStatusClass(fullExtractionStatus)}`}
                                                                            title={techValidationStatus === 'Pending' ? 'Technical Validation must be started first' : ''}
                                                                        >
                                                                            <option value="Pending">Not Started</option>
                                                                            <option value="In Progress">In Progress</option>
                                                                            {techValidationStatus === 'Completed' && (
                                                                                <option value="Completed">Completed</option>
                                                                            )}
                                                                        </select>
                                                                        <FaChevronDown className="status-select-arrow" />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* 4. Connect Integration */}
                                                            <td data-label="Connect Integration">
                                                                {!isCancelled && (
                                                                    <div className="status-select-wrapper">
                                                                        <select
                                                                            value={integrationStatus}
                                                                            onChange={(e) => handleStatusChange(iface, 'Connect Integration', e.target.value)}
                                                                            disabled={isUpdating || !(isAdmin || isManager) || fullExtractionStatus === 'Pending'}
                                                                            className={`status-select ${getStatusClass(integrationStatus)}`}
                                                                            title={fullExtractionStatus === 'Pending' ? 'Full Extraction must be completed first' : ''}
                                                                        >
                                                                            <option value="Pending">Not Started</option>
                                                                            <option value="In Progress">In Progress</option>
                                                                            {fullExtractionStatus === 'Completed' && (
                                                                                <option value="Completed">Completed</option>
                                                                            )}
                                                                        </select>
                                                                        <FaChevronDown className="status-select-arrow" />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* 5. Upload in RELEX */}
                                                            <td data-label="Upload in RELEX">
                                                                {!isCancelled && (
                                                                    <div className="status-select-wrapper">
                                                                        <select
                                                                            value={relexStatus}
                                                                            onChange={(e) => handleStatusChange(iface, 'Upload in RELEX', e.target.value)}
                                                                            disabled={isUpdating || !(isAdmin || isManager) || fullExtractionStatus === 'Pending'}
                                                                            className={`status-select ${getStatusClass(relexStatus)}`}
                                                                            title={fullExtractionStatus === 'Pending' ? 'Full Extraction must be completed first' : ''}
                                                                        >
                                                                            <option value="Pending">Not Started</option>
                                                                            <option value="In Progress">In Progress</option>
                                                                            {fullExtractionStatus === 'Completed' && (
                                                                                <option value="Completed">Completed</option>
                                                                            )}
                                                                        </select>
                                                                        <FaChevronDown className="status-select-arrow" />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            {/* 6. Functional Validation */}
                                                            <td data-label="Func Validation">
                                                                {!isCancelled && (
                                                                    <div className="status-select-wrapper">
                                                                        <select
                                                                            value={functionalStatus}
                                                                            onChange={(e) => handleStatusChange(iface, 'Functional Validation', e.target.value)}
                                                                            disabled={isUpdating || !(isAdmin || isEditor || isManager) || fullExtractionStatus === 'Pending'}
                                                                            className={`status-select ${getStatusClass(functionalStatus)}`}
                                                                            title={fullExtractionStatus === 'Pending' ? 'Full Extraction must be completed first' : ''}
                                                                        >
                                                                            <option value="Pending">Not Started</option>
                                                                            <option value="In Progress">In Progress</option>
                                                                            {fullExtractionStatus === 'Completed' && (
                                                                                <option value="Completed">Completed</option>
                                                                            )}
                                                                        </select>
                                                                        <FaChevronDown className="status-select-arrow" />
                                                                    </div>
                                                                )}
                                                            </td>

                                                            <td data-label="Status">
                                                                <span className="status-badge active-status">Active</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            </section>
                        )) : (
                            <div className="info-message"><p>No schedule data found for this project.</p></div>
                        )}
                    </div>
                )}
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1);
                    --blue-bg: rgba(59, 130, 246, 0.1); --blue-color: #93c5fd;
                    --yellow-bg: rgba(245, 158, 11, 0.1); --yellow-color: #fcd34d;
                    --green-bg: rgba(16, 185, 129, 0.1); --green-color: #6ee7b7;
                }

                .dashboard-header {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 2rem; background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color); box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                    gap: 1.5rem; position: sticky; top: 0; z-index: 100;
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); white-space: nowrap; }
                .logo-subtext { font-size: 0.75rem; color: var(--light-text); margin-left: 0.5rem; padding-left: 0.5rem; border-left: 1px solid var(--light-text); white-space: nowrap; }
                .header-project-selector { display: flex; align-items: center; gap: 0.5rem; flex-grow: 1; min-width: 150px; max-width: 400px; }
                .header-project-selector label { font-size: 0.9rem; font-weight: 500; color: var(--light-text); white-space: nowrap; }
                .header-project-selector select { flex-grow: 1; padding: 0.5rem 0.75rem; background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-color); font-size: 0.9rem; min-width: 100px; cursor: pointer; }
                .header-project-selector select:focus { outline: none; border-color: var(--green-accent); }
                .dashboard-header > div:last-child { flex-shrink: 0; }

                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }

                .admin-main-content { max-width: 1600px; margin: 2rem auto; padding: 0 2rem; }
                .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
                .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; }
                .back-button:hover { background: #4a5568; }
                .header-title-group { display: flex; align-items: center; flex-grow: 1; flex-wrap: wrap; gap: 1rem; }
                .page-title { font-size: 2rem; font-weight: 700; color: var(--text-color); margin: 0; line-height: 1; white-space: nowrap; }
                .project-context-inline { display: flex; align-items: center; font-size: 1rem; color: var(--light-text); font-weight: 500; white-space: nowrap; }
                .separator { margin-right: 1rem; color: var(--border-color); font-weight: 300; font-size: 1.2rem; position: relative; top: -1px; }
                .context-label { margin-right: 0.5rem; }
                .project-highlight { color: var(--green-accent); font-weight: 600; }

                .drops-container { display: flex; flex-direction: column; gap: 2.5rem; }
                .drop-section { background-color: var(--card-bg); border-radius: 12px; padding: 2rem; border: 1px solid var(--border-color); }
                .drop-title { font-size: 1.5rem; font-weight: 600; color: var(--text-color); margin-bottom: 1.5rem; border-bottom: 1px solid var(--border-color); padding-bottom: 1rem; }

                .table-wrapper { overflow-x: auto; }
                .admin-table { width: 100%; border-collapse: collapse; }
                .admin-table th, .admin-table td { padding: 0.75rem; text-align: left; border-bottom: 1px solid var(--border-color); vertical-align: middle; }
                .admin-table th { font-size: 0.75rem; text-transform: uppercase; color: var(--light-text); text-align: center; }
                .admin-table td { color: var(--text-color); }
                .admin-table tbody tr:hover { background-color: #374151; }
                .admin-table td:not(:first-child) { text-align: center; }
                .interface-name-cell { font-weight: 500; text-transform: capitalize; text-align: left !important; white-space: normal; }
                .cancellation-comment-cell { font-size: 0.85rem; font-style: italic; color: var(--light-text); white-space: normal; max-width: 200px; text-align: left !important; }

                .status-badge { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 9999px; font-weight: 600; font-size: 0.8rem; white-space: nowrap; }
                .status-badge.active-status { background-color: var(--green-bg); color: var(--green-color); }
                .status-badge.cancelled { background-color: var(--border-color); color: var(--text-color); }

                .status-select-wrapper { position: relative; display: inline-block; }
                .status-select { min-width: 130px; padding: 0.5rem 2rem 0.5rem 1rem; border-radius: 6px; border: none; font-weight: 600; cursor: pointer; -webkit-appearance: none; appearance: none; }
                .status-select-arrow { position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: inherit; opacity: 0.7; pointer-events: none; }
                .status-select option { background-color: var(--card-bg); color: var(--text-color); }
                .status-select.pending { background-color: var(--blue-bg); color: var(--blue-color); }
                .status-select.in-progress { background-color: var(--yellow-bg); color: var(--yellow-color); }
                .status-select.completed { background-color: var(--green-bg); color: var(--green-color); }
                .status-select:disabled { cursor: not-allowed; opacity: 0.4; }

                .row-updating { opacity: 0.6; pointer-events: none; }
                .row-inactive { background-color: #1a202c; color: var(--light-text); }
                .row-inactive .interface-name-cell { text-decoration: line-through; }
                .row-inactive td { opacity: 0.7; }
                .row-inactive .cancellation-comment-cell { text-decoration: none; }

                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); padding: 1rem; border-radius: 8px; text-align: center; }
                .drop-badge { background-color: var(--input-bg); border: 1px solid var(--border-color); padding: 0.2rem 0.6rem; border-radius: 4px; font-size: 0.8rem; font-weight: 600; }
                .loading-state { text-align: center; padding: 3rem; color: var(--light-text); }
                .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem auto; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                @keyframes spin { to { transform: rotate(360deg); } }

                .access-denied-container { text-align: center; padding: 4rem; max-width: 600px; margin: 4rem auto; background-color: var(--card-bg); border-radius: 8px; border: 1px solid var(--border-color); }
                .access-denied-icon { font-size: 3rem; color: #ef4444; margin-bottom: 1rem; }
                .access-denied-title { font-size: 1.75rem; color: var(--text-color); }

                .info-message { background-color: var(--card-bg); border: 1px solid var(--border-color); color: var(--light-text); padding: 1.5rem; border-radius: 8px; text-align: center; margin-top: 2rem; }

                .page-disclaimer { display: flex; width: 30vw; align-items: center; gap: 0.75rem; background-color: var(--card-bg); border: 1px solid var(--border-color); color: var(--light-text); padding: 0.75rem 1.25rem; border-radius: 8px; font-size: 0.85rem; margin-left: auto; flex-shrink: 1; }
                .page-disclaimer svg { color: var(--blue-color); flex-shrink: 0; }

                @media (max-width: 1200px) {
                    .admin-table thead { display: none; }
                    .admin-table, .admin-table tbody, .admin-table tr, .admin-table td { display: block; width: 100%; }
                    .admin-table tr { margin-bottom: 1.5rem; border: 1px solid var(--border-color); border-radius: 8px; padding: 0.5rem; }
                    .admin-table tbody tr { border-top: none; }
                    .admin-table td { display: flex; justify-content: space-between; align-items: center; text-align: right; padding: 0.75rem; border-top: 1px solid var(--border-color); }
                    .admin-table tr td:first-child { border-top: none; }
                    .admin-table td::before { content: attr(data-label); text-align: left; font-weight: 600; color: var(--light-text); }
                    .interface-name-cell { font-size: 1.2rem; font-weight: 700; background: var(--dark-bg); margin: -0.5rem -0.5rem 0.5rem -0.5rem; padding: 1rem; border-radius: 8px 8px 0 0; border-bottom: 1px solid var(--border-color); }
                    .interface-name-cell::before { display: none; }
                }

                @media (max-width: 768px) {
                    .dashboard-header { flex-direction: column; align-items: stretch; padding: 1rem; }
                    .page-header { flex-wrap: wrap; }
                    .page-disclaimer { margin-left: 0; width: 100%; order: 3; }
                    .header-project-selector { max-width: none; margin-top: 0.5rem; margin-bottom: 0.5rem; }
                    .logo-section { justify-content: center; }
                }
            `}</style>
        </div>
    );
}